import { stepsWithPdfTemplates, workflowsApi, type WorkflowTemplate } from '@/api/workflows';

import { collectFilledFields } from './collectFilledFields';
import { type FieldValue } from './FieldRenderer';
import { fillPdfWithValues } from './fillPdf';
import { renderFilename } from './renderFilename';

/**
 * One row on the PDF Generation step — one PDF the advisor will end up with.
 * Language-picked source key + a rendered filename for the download / tab
 * title. See `collectPdfDocs` below for how these are pulled from the
 * template.
 */
export type PdfDoc = {
  entryId: string;
  label: string;
  fileName: string;
  sourceStepTitle: string;
  /** Presigned-URL-fetchable S3 key for the language-picked template.
   *  Null = originator never uploaded a file for this entry in either
   *  language, so there's nothing to preview or download. */
  previewKey: string | null;
};

/**
 * Flatten every PDF source template across the workflow into a single list.
 * Picks the current-language file key with a fallback to the other language
 * (matches the existing PdfGenerationStep behavior — a single-locale author
 * shouldn't leave an empty download slot).
 */
export function collectPdfDocs(
  template: WorkflowTemplate,
  language: 'en' | 'fr',
  values: Record<string, FieldValue> = {},
): PdfDoc[] {
  const isFr = language === 'fr';
  return stepsWithPdfTemplates(template).flatMap((s) =>
    s.fileEntries.map<PdfDoc>((e) => {
      const label = e.label;
      // Filename template wins when authored — resolves
      // `{field:<internalName>}` + `{today:<format>}` tokens against
      // the current answers. Blank / unset template falls back to
      // the label so pre-templating workflows keep their filenames.
      const fileName = renderFilename(e.filenameTemplate, label, values, template);
      return {
        entryId: e.id,
        label,
        fileName,
        sourceStepTitle: (isFr ? s.titleFr : s.titleEn) || s.titleEn || s.titleFr,
        previewKey: isFr
          ? (e.frFileKey ?? e.enFileKey ?? null)
          : (e.enFileKey ?? e.frFileKey ?? null),
      };
    }),
  );
}

/**
 * Fetch the source PDF from S3 via a presigned URL, apply the advisor's
 * answers to any AcroForm field whose name matches a `WorkflowField`'s
 * `internalName`, and return the filled bytes as a Blob URL suitable for
 * opening in a new tab or triggering a download.
 *
 * Caller is responsible for `URL.revokeObjectURL` after the download / tab
 * open completes — leaking blob URLs across a long preview session keeps
 * every filled PDF pinned in memory.
 */
export async function buildFilledPdfBlobUrl(
  templateId: string,
  template: WorkflowTemplate,
  key: string,
  values: Record<string, FieldValue>,
  /** The PDF template entry id being filled — scopes which field mappings
   *  apply, so a field mapped only to another PDF doesn't bleed in here. */
  entryId: string,
): Promise<string> {
  const url = await workflowsApi.createDownloadUrl(templateId, key);
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch PDF template (${res.status})`);
  }
  const src = await res.arrayBuffer();
  const fills = collectFilledFields(template, values, entryId);
  const filled = await fillPdfWithValues(src, fills);
  // pdf-lib returns a Uint8Array typed against ArrayBufferLike; copy into a
  // fresh ArrayBuffer so the Blob ctor's stricter ArrayBuffer typing passes.
  const bytes = new Uint8Array(filled.byteLength);
  bytes.set(filled);
  const blob = new Blob([bytes.buffer], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

/**
 * Trigger a download via a temporary anchor. Revokes the object URL after
 * the click fires — the browser has by then already committed to the
 * download stream so revocation is safe.
 */
export function triggerBlobDownload(blobUrl: string, fileName: string) {
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a tick to bind the stream, then release.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000);
}
